/**
 * MoleculeViewer - Wrapper around 3Dmol.js GLViewer
 *
 * Provides a simplified API for molecular visualization
 * Extended from simple-viewer with PF MolView-specific features
 */

import type {
  RenderStyle,
  LabelType,
  ColorScheme,
  SymmetryDisplayMode,
  ViewerConfig,
  FogConfig,
  TubeStyleOptions,
  RibbonStyleOptions,
} from './types';

import {
  distance,
  angleDegrees,
  dihedralDegrees,
  midpoint,
  type Vec3,
  type Measurement,
} from './geometry';

import {
  create3DmolChargeColorFunc,
  create3DmolResidueChargeColorFunc,
  create3DmolResidueTypeColorFunc,
  create3DmolSymmetryColorFunc,
  create3DmolRingColorFunc,
  create3DmolRingBondsColorFunc,
  create3DmolBondOrderColorFunc,
} from './colors';

import {
  detectSymmetry,
  detectRings,
  type SymmetryResult,
  type RingInfo,
  type AtomData,
} from './symmetry-detection';

import { logger, LogCategories } from './logger';

// 3Dmol is loaded globally via CDN
declare const $3Dmol: any;

/**
 * Element colors matching desktop PF MolView (Jmol color scheme)
 */
const ELEMENT_COLORS: Record<string, string> = {
  H: '#FFFFFF',
  C: '#909090',
  N: '#3050F8',
  O: '#FF0D0D',
  S: '#FFFF30',
  P: '#FF8000',
  F: '#90E050',
  Cl: '#1FF01F',
  Br: '#A62929',
  I: '#940094',
};

/**
 * Background color presets
 */
const BACKGROUND_PRESETS: Record<string, string> = {
  black: '#000000',
  white: '#FFFFFF',
  grey: '#808080',
};

export class MoleculeViewer {
  private viewer: any;
  private currentStyle: RenderStyle = 'stick';
  private currentColorScheme: ColorScheme = 'element';
  private showHydrogens: boolean = true;
  private fogEnabled: boolean = false;
  private fogConfig: FogConfig = {
    enabled: false,
    density: 0.5,
    near: 0,
    far: 100,
  };
  private isSpinning: boolean = false;
  private spinDirection: 'x' | 'y' | 'z' = 'y';
  private spinSpeed: number = 1;
  private labelsVisible: boolean = false;
  private currentLabelType: LabelType = 'element';
  // Unified storage: each measurement with its associated 3D shapes and labels
  private measurementData: { measurement: Measurement; shapes: any[]; labels: any[] }[] = [];
  private quickSelectEnabled: boolean = false;
  private selectedAtoms: number[] = [];
  private atomClickCallback: ((atom: any) => void) | null = null;
  private selectionProgressCallback: ((count: number) => void) | null = null;

  // Symmetry-related properties
  private symmetryResult: SymmetryResult | null = null;
  private ringInfo: RingInfo | null = null;
  private symmetryDisplayMode: SymmetryDisplayMode = 'symmetry';
  private selectedSymmetryGroupIndex: number = 0;
  private selectedRingIndex: number = -1; // -1 means show all rings

  constructor(containerId: string, config: ViewerConfig = {}) {
    logger.info(LogCategories.VIEWER, 'Initializing MoleculeViewer', { containerId, config });

    const element = document.getElementById(containerId);
    if (!element) {
      const error = new Error(`Container element #${containerId} not found`);
      logger.error(LogCategories.VIEWER, 'Container not found', { containerId });
      throw error;
    }

    try {
      // Create the 3Dmol viewer
      this.viewer = $3Dmol.createViewer(element, {
        backgroundColor: config.backgroundColor || BACKGROUND_PRESETS.grey,
        antialias: config.antialias ?? true,
      });

      // Apply initial fog settings
      if (config.showFog) {
        this.setFog(true, config.fogDensity);
      }

      logger.info(LogCategories.VIEWER, 'MoleculeViewer initialized successfully');
    } catch (err) {
      logger.exception(LogCategories.VIEWER, 'Failed to create 3Dmol viewer', err as Error);
      throw err;
    }
  }

  // =========================================================================
  // Core Methods (from simple-viewer)
  // =========================================================================

  /**
   * Load a molecule from string data
   */
  loadMolecule(data: string, format: string): void {
    logger.debug(LogCategories.VIEWER, `Loading molecule`, { format, dataLength: data.length });

    try {
      this.viewer.removeAllModels();
      this.viewer.addModel(data, format);
      this.applyStyle(this.currentStyle);
      this.applyHydrogenVisibility();
      this.viewer.zoomTo();
      this.viewer.render();

      // Re-apply clickable state if quick select is enabled
      // (removeAllModels resets clickable handlers)
      if (this.quickSelectEnabled) {
        this.viewer.setClickable({}, true, (atom: any) => {
          this.handleAtomClick(atom);
        });
        this.viewer.render();
      }

      logger.debug(LogCategories.VIEWER, 'Molecule loaded successfully');
    } catch (err) {
      logger.exception(LogCategories.VIEWER, 'Failed to load molecule', err as Error);
    }
  }

  /**
   * Apply a rendering style to all atoms
   */
  applyStyle(style: RenderStyle): void {
    logger.debug(LogCategories.RENDER, `Applying style: ${style}`, {
      colorScheme: this.currentColorScheme,
      showHydrogens: this.showHydrogens,
    });

    try {
      this.currentStyle = style;
      const colorScheme = this.getColorSchemeSpec();

      switch (style) {
      case 'tube':
        // Tube style: thicker sticks with small spheres at atoms
        // Matches verachem TubeMolecule with tubeThickness=0.2, sphereRadius=0.2
        this.viewer.setStyle({}, {
          stick: { ...colorScheme, radius: 0.2 },
          sphere: { ...colorScheme, scale: 0.2 },
        });
        break;

      case 'ribbon':
        // Ribbon style for proteins - smooth tube-like backbone
        // Uses cartoon with ribbon:true for constant-width tube appearance
        this.viewer.setStyle({}, {
          cartoon: { ...colorScheme, style: 'oval', ribbon: true, thickness: 0.5 },
        });
        // Also show non-protein atoms (ligands, etc.) as sticks
        this.viewer.setStyle({ hetflag: true }, {
          stick: { ...colorScheme, radius: 0.15 },
        });
        break;

      case 'stick':
        this.viewer.setStyle({}, {
          stick: { ...colorScheme, radius: 0.15 },
        });
        break;

      case 'line':
        this.viewer.setStyle({}, {
          line: { ...colorScheme },
        });
        break;

      case 'sphere':
        this.viewer.setStyle({}, {
          sphere: { ...colorScheme, scale: 0.25 },
        });
        break;

      case 'cartoon':
        // Cartoon shows secondary structure (helices, sheets, loops)
        // Uses default rectangular cross-section with arrows on sheets
        this.viewer.setStyle({}, {
          cartoon: { ...colorScheme, arrows: true, tubes: true, thickness: 0.4 },
        });
        // Show heteroatoms (ligands) as sticks
        this.viewer.setStyle({ hetflag: true }, {
          stick: { ...colorScheme, radius: 0.15 },
        });
        break;

      case 'cross':
        this.viewer.setStyle({}, {
          cross: { ...colorScheme, linewidth: 2 },
        });
        break;

      default:
        // Default to stick
        this.viewer.setStyle({}, {
          stick: { ...colorScheme, radius: 0.15 },
        });
      }

      this.applyHydrogenVisibility();
      this.viewer.render();
    } catch (err) {
      logger.exception(LogCategories.RENDER, `Failed to apply style: ${style}`, err as Error);
    }
  }

  /**
   * Get the color scheme specification for 3Dmol
   * Uses color functions ported from verachem3/graphics/molecularGraphics.py
   */
  private getColorSchemeSpec(): any {
    switch (this.currentColorScheme) {
      case 'element':
        return { colorscheme: 'Jmol' };
      case 'chain':
        return { colorscheme: 'chain' };
      case 'residueType':
        // Use custom function for residue type coloring (hydrophobic vs polar)
        return { colorfunc: create3DmolResidueTypeColorFunc() };
      case 'charge':
        // Use custom function for charge-based coloring (red-white-blue gradient)
        return { colorfunc: create3DmolChargeColorFunc(-0.5, 0.5) };
      case 'residueCharge':
        // Color by residue formal charge (ASP/GLU=red, LYS/ARG=blue)
        return { colorfunc: create3DmolResidueChargeColorFunc() };
      case 'symmetry':
        // Use symmetry-based coloring from Color Symmetry panel
        return this.getSymmetryColorSpec();
      case 'custom':
      default:
        return { colorscheme: 'Jmol' };
    }
  }

  /**
   * Get the color spec for symmetry-based coloring based on current mode
   */
  private getSymmetryColorSpec(): any {
    switch (this.symmetryDisplayMode) {
      case 'symmetry':
        if (this.symmetryResult) {
          return { colorfunc: create3DmolSymmetryColorFunc(this.symmetryResult) };
        }
        break;
      case 'ring':
        if (this.ringInfo) {
          return { colorfunc: create3DmolRingColorFunc(this.ringInfo, this.selectedRingIndex) };
        }
        break;
      case 'bondOrder':
        return { colorfunc: create3DmolBondOrderColorFunc() };
      case 'ringBonds':
        if (this.ringInfo) {
          return { colorfunc: create3DmolRingBondsColorFunc(this.ringInfo) };
        }
        break;
    }
    // Fallback to element coloring if no data available
    return { colorscheme: 'Jmol' };
  }

  /**
   * Set the color scheme
   */
  setColorScheme(scheme: ColorScheme): void {
    this.currentColorScheme = scheme;
    this.applyStyle(this.currentStyle);
  }

  /**
   * Set background color
   */
  setBackgroundColor(color: string): void {
    // Handle preset names
    const bgColor = BACKGROUND_PRESETS[color] || color;
    this.viewer.setBackgroundColor(bgColor);
  }

  /**
   * Reset the view to fit all molecules
   */
  resetView(): void {
    this.viewer.zoomTo();
    this.viewer.render();
  }

  /**
   * Center the view on a specific point
   */
  centerView(x?: number, y?: number, z?: number): void {
    if (x !== undefined && y !== undefined && z !== undefined) {
      this.viewer.center({ x, y, z });
    } else {
      this.viewer.zoomTo();
    }
    this.viewer.render();
  }

  /**
   * Clear all models from the viewer
   */
  clear(): void {
    this.viewer.removeAllModels();
    this.viewer.render();
  }

  /**
   * Get current render style
   */
  getStyle(): RenderStyle {
    return this.currentStyle;
  }

  /**
   * Render the scene
   */
  render(): void {
    this.viewer.render();
  }

  /**
   * Get PNG image data URL
   */
  getImage(): string {
    return this.viewer.pngURI();
  }

  // =========================================================================
  // PF MolView-specific Methods
  // =========================================================================

  /**
   * Set hydrogen visibility
   */
  setHydrogensVisible(visible: boolean): void {
    logger.debug(LogCategories.VIEWER, `Setting hydrogen visibility: ${visible}`);
    this.showHydrogens = visible;
    // Reapply the current style to properly show/hide hydrogens
    this.applyStyle(this.currentStyle);
  }

  /**
   * Get hydrogen visibility state
   */
  getHydrogensVisible(): boolean {
    return this.showHydrogens;
  }

  /**
   * Apply hydrogen visibility to current style
   * Called after main style is applied
   */
  private applyHydrogenVisibility(): void {
    if (!this.showHydrogens) {
      // Hide hydrogens by setting them to hidden style
      this.viewer.setStyle({ elem: 'H' }, { hidden: true });
    }
    // Note: When showHydrogens is true, hydrogens get their style
    // from the main applyStyle() call, so no action needed here
  }

  /**
   * Toggle hydrogen visibility
   */
  toggleHydrogens(): boolean {
    this.setHydrogensVisible(!this.showHydrogens);
    return this.showHydrogens;
  }

  /**
   * Set fog settings
   */
  setFog(enabled: boolean, density?: number, near?: number, far?: number): void {
    this.fogEnabled = enabled;
    this.fogConfig.enabled = enabled;

    if (density !== undefined) {
      this.fogConfig.density = density;
    }
    if (near !== undefined) {
      this.fogConfig.near = near;
    }
    if (far !== undefined) {
      this.fogConfig.far = far;
    }

    if (enabled) {
      // 3Dmol fog implementation
      this.viewer.setFog(true);
    } else {
      this.viewer.setFog(false);
    }
    this.viewer.render();
  }

  /**
   * Get fog configuration
   */
  getFogConfig(): FogConfig {
    return { ...this.fogConfig };
  }

  /**
   * Toggle fog
   */
  toggleFog(): boolean {
    this.setFog(!this.fogEnabled);
    return this.fogEnabled;
  }

  /**
   * Enable/disable spinning
   */
  spin(enable: boolean, axis?: 'x' | 'y' | 'z', speed?: number): void {
    this.isSpinning = enable;

    if (axis) {
      this.spinDirection = axis;
    }
    if (speed !== undefined) {
      this.spinSpeed = speed;
    }

    if (enable) {
      this.viewer.spin(this.spinDirection, this.spinSpeed);
    } else {
      this.viewer.spin(false);
    }
  }

  /**
   * Toggle spinning
   */
  toggleSpin(): boolean {
    this.spin(!this.isSpinning);
    return this.isSpinning;
  }

  /**
   * Get spin state
   */
  isSpinEnabled(): boolean {
    return this.isSpinning;
  }

  /**
   * Add atom labels
   */
  addAtomLabels(type: LabelType, atomIndices?: number[]): void {
    this.currentLabelType = type;
    this.labelsVisible = true;

    // Remove existing labels
    this.viewer.removeAllLabels();

    const model = this.viewer.getModel();
    if (!model) return;

    // Get all atoms or specific indices
    const selector = atomIndices ? { serial: atomIndices } : {};
    const atoms = model.selectedAtoms(selector);

    // Label style options
    const labelOptions = {
      font: 'Arial',
      fontSize: 12,
      fontColor: 'black',
      backgroundColor: 'white',
      backgroundOpacity: 0.8,
      showBackground: true,
    };

    // Add label to each atom based on type
    for (const atom of atoms) {
      const labelText = this.getAtomLabelText(atom, type);
      if (labelText) {
        this.viewer.addLabel(labelText, {
          ...labelOptions,
          position: { x: atom.x, y: atom.y, z: atom.z },
        });
      }
    }

    this.viewer.render();
  }

  /**
   * Get label text for an atom based on label type
   */
  private getAtomLabelText(atom: any, type: LabelType): string {
    switch (type) {
      case 'element':
        return atom.elem || '';
      case 'atomNumber':
        return atom.serial?.toString() || '';
      case 'atomName':
        return atom.atom || atom.elem || '';
      case 'residueName':
        return atom.resn || '';
      case 'residueNumber':
        return atom.resi?.toString() || '';
      case 'partialCharge':
        return atom.partialCharge != null ? atom.partialCharge.toFixed(3) : '';
      case 'formalCharge':
        return atom.formalCharge != null ? atom.formalCharge.toString() : '';
      default:
        return atom.elem || '';
    }
  }

  /**
   * Clear all labels
   */
  clearLabels(): void {
    this.labelsVisible = false;
    this.viewer.removeAllLabels();
    this.viewer.render();
  }

  /**
   * Toggle labels
   */
  toggleLabels(type?: LabelType): boolean {
    if (this.labelsVisible) {
      this.clearLabels();
    } else {
      this.addAtomLabels(type || this.currentLabelType);
    }
    return this.labelsVisible;
  }

  /**
   * Get labels visibility state
   */
  areLabelsVisible(): boolean {
    return this.labelsVisible;
  }

  /**
   * Set rotation center to a specific atom or point
   */
  setRotationCenter(x: number, y: number, z: number): void {
    this.viewer.setRotationCenter({ x, y, z });
  }

  /**
   * Zoom to a specific level
   */
  zoom(level: number): void {
    this.viewer.zoom(level);
    this.viewer.render();
  }

  /**
   * Get the 3Dmol viewer instance for advanced operations
   */
  getViewer(): any {
    return this.viewer;
  }

  // =========================================================================
  // VectorMolecule-Style Rendering (ported from verachem3/graphics/molecularGraphics.py)
  // =========================================================================

  /**
   * Set tube style with custom options
   * Ported from verachem TubeMolecule class (molecularGraphics.py:4574)
   *
   * @param options - Tube rendering options
   *   - radius: Tube thickness (default 0.2, matching verachem tubeThickness)
   *   - segments: Not used in 3Dmol.js (for future WebGL implementation)
   *   - colorScheme: Color scheme to apply
   */
  setTubeStyle(options?: TubeStyleOptions): void {
    const radius = options?.radius ?? 0.2;
    const colorScheme = options?.colorScheme ?? this.currentColorScheme;

    // Temporarily set color scheme if specified
    const prevScheme = this.currentColorScheme;
    if (colorScheme !== prevScheme) {
      this.currentColorScheme = colorScheme;
    }

    const colorSpec = this.getColorSchemeSpec();

    // Tube style: thick sticks with spheres at atom positions
    this.viewer.setStyle({}, {
      stick: { ...colorSpec, radius: radius },
      sphere: { ...colorSpec, scale: radius },
    });

    this.applyHydrogenVisibility();
    this.viewer.render();

    // Restore previous color scheme
    this.currentColorScheme = prevScheme;
    this.currentStyle = 'tube';
  }

  /**
   * Set ribbon style with custom options
   * Uses 3Dmol.js cartoon rendering for protein secondary structure
   *
   * @param options - Ribbon rendering options
   *   - width: Ribbon width (affects cartoon thickness)
   *   - smoothness: Spline smoothness (not directly supported in 3Dmol)
   *   - colorScheme: Color scheme to apply
   */
  setRibbonStyle(options?: RibbonStyleOptions): void {
    const colorScheme = options?.colorScheme ?? this.currentColorScheme;

    // Temporarily set color scheme if specified
    const prevScheme = this.currentColorScheme;
    if (colorScheme !== prevScheme) {
      this.currentColorScheme = colorScheme;
    }

    const colorSpec = this.getColorSchemeSpec();

    // Ribbon/Cartoon for protein backbone - tube-like with constant width
    this.viewer.setStyle({}, {
      cartoon: { ...colorSpec, style: 'oval', ribbon: true, thickness: options?.width ?? 0.5 },
    });

    // Show heteroatoms (ligands, waters) as sticks
    this.viewer.setStyle({ hetflag: true }, {
      stick: { ...colorSpec, radius: 0.15 },
    });

    this.applyHydrogenVisibility();
    this.viewer.render();

    // Restore previous color scheme
    this.currentColorScheme = prevScheme;
    this.currentStyle = 'ribbon';
  }

  // =========================================================================
  // Measurement Methods (ported from verachem3/misc/numericTools.py)
  // =========================================================================

  /**
   * Get atom position from current model by serial number
   */
  private getAtomPosition(serial: number): Vec3 | null {
    const model = this.viewer.getModel();
    if (!model) return null;

    const atoms = model.selectedAtoms({ serial: serial });
    if (atoms.length === 0) return null;

    const atom = atoms[0];
    return [atom.x, atom.y, atom.z];
  }

  /**
   * Add distance measurement between two atoms
   * Uses geometry.distance() ported from numericTools.py
   */
  addDistanceMeasurement(atom1: number, atom2: number): Measurement | null {
    const p1 = this.getAtomPosition(atom1);
    const p2 = this.getAtomPosition(atom2);

    if (!p1 || !p2) {
      console.warn(`Could not find atoms ${atom1} and/or ${atom2}`);
      return null;
    }

    const dist = distance(p1, p2);
    const mid = midpoint(p1, p2);

    const measurement: Measurement = {
      type: 'distance',
      atom1,
      atom2,
      value: dist,
      label: `${dist.toFixed(2)} \u00C5`,
    };

    // Draw line between atoms
    const cylinder = this.viewer.addCylinder({
      start: { x: p1[0], y: p1[1], z: p1[2] },
      end: { x: p2[0], y: p2[1], z: p2[2] },
      radius: 0.05,
      color: 'yellow',
      dashed: true,
    });

    // Add label at midpoint
    const label = this.viewer.addLabel(measurement.label, {
      position: { x: mid[0], y: mid[1], z: mid[2] },
      backgroundColor: 'black',
      backgroundOpacity: 0.7,
      fontColor: 'yellow',
      fontSize: 14,
    });

    // Store measurement with its shapes and labels separately
    this.measurementData.push({ measurement, shapes: [cylinder], labels: [label] });
    this.viewer.render();

    return measurement;
  }

  /**
   * Add angle measurement between three atoms
   * Uses geometry.angleDegrees() ported from numericTools.py
   */
  addAngleMeasurement(atom1: number, atom2: number, atom3: number): Measurement | null {
    const p1 = this.getAtomPosition(atom1);
    const p2 = this.getAtomPosition(atom2);
    const p3 = this.getAtomPosition(atom3);

    if (!p1 || !p2 || !p3) {
      console.warn(`Could not find atoms ${atom1}, ${atom2}, and/or ${atom3}`);
      return null;
    }

    const ang = angleDegrees(p1, p2, p3);

    const measurement: Measurement = {
      type: 'angle',
      atom1,
      atom2,
      atom3,
      value: ang,
      label: `${ang.toFixed(1)}\u00B0`,
    };

    // Draw lines from vertex to each end atom
    const line1 = this.viewer.addCylinder({
      start: { x: p2[0], y: p2[1], z: p2[2] },
      end: { x: p1[0], y: p1[1], z: p1[2] },
      radius: 0.04,
      color: 'cyan',
      dashed: true,
    });

    const line2 = this.viewer.addCylinder({
      start: { x: p2[0], y: p2[1], z: p2[2] },
      end: { x: p3[0], y: p3[1], z: p3[2] },
      radius: 0.04,
      color: 'cyan',
      dashed: true,
    });

    // Add label near vertex
    const labelPos: Vec3 = [
      p2[0] + (p1[0] - p2[0]) * 0.3 + (p3[0] - p2[0]) * 0.3,
      p2[1] + (p1[1] - p2[1]) * 0.3 + (p3[1] - p2[1]) * 0.3,
      p2[2] + (p1[2] - p2[2]) * 0.3 + (p3[2] - p2[2]) * 0.3,
    ];

    const label = this.viewer.addLabel(measurement.label, {
      position: { x: labelPos[0], y: labelPos[1], z: labelPos[2] },
      backgroundColor: 'black',
      backgroundOpacity: 0.7,
      fontColor: 'cyan',
      fontSize: 14,
    });

    // Store measurement with its shapes and labels separately
    this.measurementData.push({ measurement, shapes: [line1, line2], labels: [label] });
    this.viewer.render();

    return measurement;
  }

  /**
   * Add dihedral measurement between four atoms
   * Uses geometry.dihedralDegrees() ported from numericTools.py
   */
  addDihedralMeasurement(
    atom1: number,
    atom2: number,
    atom3: number,
    atom4: number
  ): Measurement | null {
    const p1 = this.getAtomPosition(atom1);
    const p2 = this.getAtomPosition(atom2);
    const p3 = this.getAtomPosition(atom3);
    const p4 = this.getAtomPosition(atom4);

    if (!p1 || !p2 || !p3 || !p4) {
      console.warn(`Could not find atoms ${atom1}, ${atom2}, ${atom3}, and/or ${atom4}`);
      return null;
    }

    const dih = dihedralDegrees(p1, p2, p3, p4);

    const measurement: Measurement = {
      type: 'dihedral',
      atom1,
      atom2,
      atom3,
      atom4,
      value: dih,
      label: `${dih.toFixed(1)}\u00B0`,
    };

    // Draw lines connecting all four atoms
    const shapes: any[] = [];
    const points = [p1, p2, p3, p4];
    for (let i = 0; i < 3; i++) {
      const line = this.viewer.addCylinder({
        start: { x: points[i][0], y: points[i][1], z: points[i][2] },
        end: { x: points[i + 1][0], y: points[i + 1][1], z: points[i + 1][2] },
        radius: 0.04,
        color: 'magenta',
        dashed: true,
      });
      shapes.push(line);
    }

    // Add label at midpoint of central bond
    const mid = midpoint(p2, p3);
    const label = this.viewer.addLabel(measurement.label, {
      position: { x: mid[0], y: mid[1], z: mid[2] },
      backgroundColor: 'black',
      backgroundOpacity: 0.7,
      fontColor: 'magenta',
      fontSize: 14,
    });

    // Store measurement with its shapes and labels separately
    this.measurementData.push({ measurement, shapes, labels: [label] });
    this.viewer.render();

    return measurement;
  }

  /**
   * Clear all measurements
   */
  clearMeasurements(): void {
    // Remove all shapes and labels separately
    for (const { shapes, labels } of this.measurementData) {
      for (const shape of shapes) {
        this.viewer.removeShape(shape);
      }
      for (const label of labels) {
        this.viewer.removeLabel(label);
      }
    }
    this.measurementData = [];
    this.viewer.render();
  }

  /**
   * Get all current measurements
   */
  getMeasurements(): Measurement[] {
    return this.measurementData.map(d => d.measurement);
  }

  /**
   * Delete a specific measurement by index
   */
  deleteMeasurement(index: number): boolean {
    if (index < 0 || index >= this.measurementData.length) {
      return false;
    }

    const { shapes, labels } = this.measurementData[index];
    for (const shape of shapes) {
      this.viewer.removeShape(shape);
    }
    for (const label of labels) {
      this.viewer.removeLabel(label);
    }

    this.measurementData.splice(index, 1);
    this.viewer.render();

    logger.debug(LogCategories.MEASURE, `Deleted measurement at index ${index}`);
    return true;
  }

  /**
   * Set callback for selection progress updates
   */
  onSelectionProgress(callback: (count: number) => void): void {
    this.selectionProgressCallback = callback;
  }

  // =========================================================================
  // Atom Selection Methods
  // =========================================================================

  /**
   * Highlight selected atoms
   */
  highlightAtoms(atomIndices: number[]): void {
    // Add selection highlighting
    this.viewer.setStyle(
      { serial: atomIndices },
      {
        stick: { colorscheme: 'Jmol', radius: 0.2 },
        sphere: { colorscheme: 'Jmol', scale: 0.3 },
      }
    );
    this.viewer.render();
  }

  /**
   * Clear atom highlighting
   */
  clearHighlight(): void {
    this.applyStyle(this.currentStyle);
  }

  // =========================================================================
  // Surface Methods (for future VSurface-like functionality)
  // =========================================================================

  /**
   * Add molecular surface
   */
  addSurface(type: 'VDW' | 'SAS' | 'SES' | 'MS' = 'VDW', opacity: number = 0.8): void {
    const surfaceType = {
      VDW: $3Dmol.SurfaceType.VDW,
      SAS: $3Dmol.SurfaceType.SAS,
      SES: $3Dmol.SurfaceType.SES,
      MS: $3Dmol.SurfaceType.MS,
    }[type];

    this.viewer.addSurface(surfaceType, {
      opacity: opacity,
      colorscheme: 'Jmol',
    });
    this.viewer.render();
  }

  /**
   * Remove all surfaces
   */
  removeSurfaces(): void {
    this.viewer.removeAllSurfaces();
    this.viewer.render();
  }

  // =========================================================================
  // Quick Select / Atom Click Methods
  // =========================================================================

  /**
   * Enable or disable quick select mode
   * When enabled, clicking atoms will select them for measurements
   */
  setQuickSelectEnabled(enabled: boolean): void {
    logger.debug(LogCategories.SELECT, `Quick select ${enabled ? 'enabled' : 'disabled'}`);
    this.quickSelectEnabled = enabled;

    if (enabled) {
      // Set up click handler
      this.viewer.setClickable({}, true, (atom: any) => {
        this.handleAtomClick(atom);
      });
      this.viewer.render();
    } else {
      // Remove click handler
      this.viewer.setClickable({}, false);
      this.clearSelection();
    }
  }

  /**
   * Check if quick select is enabled
   */
  isQuickSelectEnabled(): boolean {
    return this.quickSelectEnabled;
  }

  /**
   * Handle atom click event
   */
  private handleAtomClick(atom: any): void {
    if (!atom) {
      logger.debug(LogCategories.SELECT, 'Click on empty space');
      return;
    }

    logger.debug(LogCategories.SELECT, 'Atom clicked', {
      serial: atom.serial,
      elem: atom.elem,
      resn: atom.resn,
      resi: atom.resi,
      x: atom.x,
      y: atom.y,
      z: atom.z,
    });

    // Add to selected atoms
    this.selectedAtoms.push(atom.serial);

    // Notify selection progress listeners
    if (this.selectionProgressCallback) {
      this.selectionProgressCallback(this.selectedAtoms.length);
    }

    // Highlight the clicked atom
    this.viewer.setStyle({ serial: atom.serial }, {
      stick: { colorscheme: 'Jmol', radius: 0.2 },
      sphere: { color: 'yellow', scale: 0.3 },
    });
    this.viewer.render();

    // Call custom callback if set
    if (this.atomClickCallback) {
      this.atomClickCallback(atom);
    }

    // Auto-measure based on number of selected atoms
    if (this.selectedAtoms.length === 2) {
      const measurement = this.addDistanceMeasurement(
        this.selectedAtoms[0],
        this.selectedAtoms[1]
      );
      if (measurement) {
        logger.info(LogCategories.MEASURE, `Distance: ${measurement.label}`);
      }
    } else if (this.selectedAtoms.length === 3) {
      const measurement = this.addAngleMeasurement(
        this.selectedAtoms[0],
        this.selectedAtoms[1],
        this.selectedAtoms[2]
      );
      if (measurement) {
        logger.info(LogCategories.MEASURE, `Angle: ${measurement.label}`);
      }
    } else if (this.selectedAtoms.length === 4) {
      const measurement = this.addDihedralMeasurement(
        this.selectedAtoms[0],
        this.selectedAtoms[1],
        this.selectedAtoms[2],
        this.selectedAtoms[3]
      );
      if (measurement) {
        logger.info(LogCategories.MEASURE, `Dihedral: ${measurement.label}`);
      }
      // Reset selection after dihedral
      this.clearSelection();
    }
  }

  /**
   * Set a custom callback for atom clicks
   */
  onAtomClick(callback: (atom: any) => void): void {
    this.atomClickCallback = callback;
  }

  /**
   * Get currently selected atom serials
   */
  getSelectedAtoms(): number[] {
    return [...this.selectedAtoms];
  }

  /**
   * Clear atom selection
   */
  clearSelection(): void {
    logger.debug(LogCategories.SELECT, 'Clearing selection', {
      previousSelection: this.selectedAtoms,
    });
    this.selectedAtoms = [];

    // Notify selection progress listeners
    if (this.selectionProgressCallback) {
      this.selectionProgressCallback(0);
    }

    // Reapply style to remove selection highlighting
    this.applyStyle(this.currentStyle);
  }

  // =========================================================================
  // Symmetry Detection Methods (Color Symmetry Panel)
  // =========================================================================

  /**
   * Analyze the current molecule for symmetry
   * Must be called after loading a molecule to enable symmetry coloring
   */
  analyzeSymmetry(includeHydrogens: boolean = false): void {
    const atoms = this.getAtomData();
    if (atoms.length === 0) {
      logger.debug(LogCategories.VIEWER, 'No atoms to analyze for symmetry');
      this.symmetryResult = null;
      this.ringInfo = null;
      return;
    }

    logger.debug(LogCategories.VIEWER, 'Analyzing symmetry', { atomCount: atoms.length, includeHydrogens });

    // Detect symmetry groups
    this.symmetryResult = detectSymmetry(atoms, includeHydrogens);
    logger.debug(LogCategories.VIEWER, 'Symmetry detection complete', {
      groups: this.symmetryResult.groups.length,
      uniqueAtoms: this.symmetryResult.uniqueAtoms.length,
    });

    // Detect rings
    this.ringInfo = detectRings(atoms);
    logger.debug(LogCategories.VIEWER, 'Ring detection complete', {
      rings: this.ringInfo.rings.length,
    });
  }

  /**
   * Get atom data from the current model for symmetry analysis
   */
  private getAtomData(): AtomData[] {
    const atoms: AtomData[] = [];

    try {
      const model = this.viewer.getModel();
      if (!model) return atoms;

      const modelAtoms = model.selectedAtoms({});
      for (const atom of modelAtoms) {
        atoms.push({
          index: atom.serial - 1, // 3Dmol uses 1-based indexing
          element: atom.elem || 'C',
          position: [atom.x, atom.y, atom.z] as Vec3,
          formalCharge: atom.formalCharge || 0,
          bonds: atom.bonds?.map((bondIdx: number, i: number) => ({
            targetIndex: bondIdx - 1,
            order: atom.bondOrder?.[i] || 1,
          })),
        });
      }
    } catch (err) {
      logger.exception(LogCategories.VIEWER, 'Error getting atom data', err as Error);
    }

    return atoms;
  }

  /**
   * Set the symmetry display mode
   */
  setSymmetryDisplayMode(mode: SymmetryDisplayMode): void {
    this.symmetryDisplayMode = mode;
    this.selectedRingIndex = -1; // Reset to show all rings
    this.selectedSymmetryGroupIndex = 0;

    if (this.currentColorScheme === 'symmetry') {
      this.applyStyle(this.currentStyle);
    }

    logger.debug(LogCategories.VIEWER, 'Symmetry display mode set', { mode });
  }

  /**
   * Get the current symmetry display mode
   */
  getSymmetryDisplayMode(): SymmetryDisplayMode {
    return this.symmetryDisplayMode;
  }

  /**
   * Get the symmetry analysis result
   */
  getSymmetryResult(): SymmetryResult | null {
    return this.symmetryResult;
  }

  /**
   * Get the ring analysis result
   */
  getRingInfo(): RingInfo | null {
    return this.ringInfo;
  }

  /**
   * Select a specific ring to highlight
   * @param index - Ring index, or -1 to show all rings
   */
  selectRing(index: number): void {
    this.selectedRingIndex = index;

    if (this.currentColorScheme === 'symmetry' && this.symmetryDisplayMode === 'ring') {
      this.applyStyle(this.currentStyle);
    }

    logger.debug(LogCategories.VIEWER, 'Ring selected', { index });
  }

  /**
   * Get the currently selected ring index
   */
  getSelectedRingIndex(): number {
    return this.selectedRingIndex;
  }

  /**
   * Navigate to next ring
   */
  nextRing(): void {
    if (!this.ringInfo || this.ringInfo.rings.length === 0) return;

    this.selectedRingIndex++;
    if (this.selectedRingIndex >= this.ringInfo.rings.length) {
      this.selectedRingIndex = -1; // Wrap to "all rings"
    }
    this.selectRing(this.selectedRingIndex);
  }

  /**
   * Navigate to previous ring
   */
  prevRing(): void {
    if (!this.ringInfo || this.ringInfo.rings.length === 0) return;

    this.selectedRingIndex--;
    if (this.selectedRingIndex < -1) {
      this.selectedRingIndex = this.ringInfo.rings.length - 1;
    }
    this.selectRing(this.selectedRingIndex);
  }

  /**
   * Select a specific symmetry group to highlight
   */
  selectSymmetryGroup(index: number): void {
    this.selectedSymmetryGroupIndex = index;
    logger.debug(LogCategories.VIEWER, 'Symmetry group selected', { index });
  }

  /**
   * Get the currently selected symmetry group index
   */
  getSelectedSymmetryGroupIndex(): number {
    return this.selectedSymmetryGroupIndex;
  }
}

// Export element colors for use in other components
export { ELEMENT_COLORS, BACKGROUND_PRESETS };
